import {useCallback, useEffect, useState} from "react";
import baseUrl from "../../../baseUrl";
import {requester} from "../../../services/requester";

// Д2 UI — дърво области→общини→землища с включване/изключване. Lazy зареждане на
// 3 нива (5520 реда не наведнъж). Бекендът: GET /admin/settlements/tree,
// PUT /settlements/active/bulk (област/община), PUT /settlements/<ekatte>/active.
const TREE = () => `${baseUrl()}/admin/settlements/tree`;

const state = (active, total) =>
    active === 0 ? 'изключена' : active === total ? 'активна' : 'частично';

export const SettlementsTree = () => {
    const request = requester();
    const [regions, setRegions] = useState([]);
    const [openMuni, setOpenMuni] = useState({});    // region -> [municipalities]
    const [openSetts, setOpenSetts] = useState({});  // "region|muni" -> [settlements]
    const [error, setError] = useState('');

    const getMuni = (region) =>
        request.get(`${TREE()}?region=${encodeURIComponent(region)}`);
    const getSetts = (region, muni) =>
        request.get(`${TREE()}?region=${encodeURIComponent(region)}&municipality=${encodeURIComponent(muni)}`);

    // Презарежда всичко отворено, за да са верни броячите след промяна.
    const refresh = useCallback(async () => {
        try {
            setError('');
            setRegions(await request.get(TREE()));
            const nm = {};
            for (const region of Object.keys(openMuni)) nm[region] = await getMuni(region);
            setOpenMuni(nm);
            const ns = {};
            for (const key of Object.keys(openSetts)) {
                const [r, m] = key.split('|');
                ns[key] = await getSetts(r, m);
            }
            setOpenSetts(ns);
        } catch (err) {
            setError(err.message || 'Грешка.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openMuni, openSetts]);

    useEffect(() => {
        request.get(TREE()).then(setRegions).catch(err => setError(err.message || 'Грешка.'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleRegion = async (region) => {
        if (openMuni[region]) {
            setOpenMuni(p => { const n = {...p}; delete n[region]; return n; });
        } else {
            setOpenMuni(p => ({...p, [region]: 'loading'}));
            const muni = await getMuni(region);
            setOpenMuni(p => ({...p, [region]: muni}));
        }
    };

    const toggleMuni = async (region, muni) => {
        const key = `${region}|${muni}`;
        if (openSetts[key]) {
            setOpenSetts(p => { const n = {...p}; delete n[key]; return n; });
        } else {
            setOpenSetts(p => ({...p, [key]: 'loading'}));
            const setts = await getSetts(region, muni);
            setOpenSetts(p => ({...p, [key]: setts}));
        }
    };

    const bulk = async (active, region, municipality) => {
        try {
            await request.put(`${baseUrl()}/settlements/active/bulk`,
                municipality ? {active, region, municipality} : {active, region});
            await refresh();
        } catch (err) { setError(err.message || 'Грешка.'); }
    };

    const toggleSettlement = async (ekatte, active) => {
        try {
            await request.put(`${baseUrl()}/settlements/${ekatte}/active`, {active});
            await refresh();
        } catch (err) { setError(err.message || 'Грешка.'); }
    };

    const bulkBtns = (active, total, region, muni) => (
        <span className="ms-2">
            <span className={`badge me-2 ${state(active, total) === 'активна' ? 'bg-success'
                : state(active, total) === 'частично' ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                {active}/{total} · {state(active, total)}
            </span>
            <button className="btn btn-sm btn-outline-success py-0" onClick={() => bulk(true, region, muni)}>Всички</button>
            <button className="btn btn-sm btn-outline-secondary py-0 ms-1" onClick={() => bulk(false, region, muni)}>Никое</button>
        </span>
    );

    return (
        <div>
            {error && <div className="alert alert-danger">{error}</div>}
            <ul className="list-unstyled mb-0">
                {regions.map(reg => (
                    <li key={reg.region} className="mb-1">
                        <span role="button" className="fw-bold" onClick={() => toggleRegion(reg.region)}>
                            {openMuni[reg.region] ? '▾' : '▸'} {reg.region}
                        </span>
                        {bulkBtns(reg.active_count, reg.total, reg.region)}

                        {Array.isArray(openMuni[reg.region]) && (
                            <ul className="list-unstyled ms-4 mt-1">
                                {openMuni[reg.region].map(m => {
                                    const key = `${reg.region}|${m.municipality}`;
                                    return (
                                        <li key={key} className="mb-1">
                                            <span role="button" onClick={() => toggleMuni(reg.region, m.municipality)}>
                                                {openSetts[key] ? '▾' : '▸'} {m.municipality}
                                            </span>
                                            {bulkBtns(m.active_count, m.total, reg.region, m.municipality)}

                                            {Array.isArray(openSetts[key]) && (
                                                <ul className="list-unstyled ms-4 mt-1">
                                                    {openSetts[key].map(s => (
                                                        <li key={s.ekatte} className="form-check">
                                                            <input className="form-check-input" type="checkbox"
                                                                   id={`s-${s.ekatte}`} checked={s.active}
                                                                   onChange={e => toggleSettlement(s.ekatte, e.target.checked)}/>
                                                            <label className="form-check-label" htmlFor={`s-${s.ekatte}`}>
                                                                {s.settlement} <span className="text-muted small">({s.ekatte})</span>
                                                            </label>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
};
